import {
  Component,
  forwardRef,
  Input,
  OnInit,
} from '@angular/core';

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  ControlContainer
} from '@angular/forms';

import { AbstractComponent } from "@components";
import { DateTimeWithMilisecondsStruct } from "@model";


@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss',
  standalone: false,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateInputComponent),
    multi: true
  }]
})
export class DateInputComponent extends AbstractComponent implements ControlValueAccessor, OnInit {

  @Input() withTime = false;
  @Input() withSeconds = false;
  @Input() withMilliseconds = false;
  @Input() formControlName?: string;
  @Input() normalColor = '#000';
  @Input() showError = false;
  @Input() onlyYear = false;

  value = '';
  overlayChars: string[] = [];
  overlayPadding: string[] = [];
  isFocused = false;
  isDisabled = false;

  private internalValue: DateTimeWithMilisecondsStruct | null = null;

  private onChange = (val: DateTimeWithMilisecondsStruct | null) => {};
  private onTouched = () => {};


  constructor(private controlContainer: ControlContainer) {
    super();
  }

  ngOnInit(): void {
    this.updateOverlay('');
  }

  writeValue(value: DateTimeWithMilisecondsStruct | null): void {
    this.internalValue = value;

    if (!value?.date) {
      this.value = '';
      this.updateOverlay('');
      return;
    }

    const formatted = this.formatValue(value);
    this.value = formatted;
    this.updateOverlay(formatted);
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }


  onInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;

    if (this.isDisabled) {
      inputElement.value = "";
      return;
    };

    this.onTouched();
    const rawValue = inputElement.value;

    const digits = rawValue.replace(/\D/g, '').substring(0, this.getMaxDigits());
    const masked = this.applyMask(digits);

    this.value = masked;
    inputElement.value = masked;

    const cursorPos = Math.min(inputElement.selectionStart || 0, masked.length);
    requestAnimationFrame(() => {
      inputElement.setSelectionRange(cursorPos, cursorPos);
    });

    this.updateOverlay(masked);

    const parsed = this.parseValue(masked);
    const isComplete = this.isInputComplete(masked);

    if (isComplete && parsed) {
      this.internalValue = parsed;
      this.onChange(parsed);
    } else {
      this.internalValue = null;
      this.onChange(null);
    }
  }

  onFocus() {
    if (this.isDisabled) return;
    this.isFocused = true;
    this.updateOverlay(this.value);
  }

  onBlur() {
    if (this.isDisabled) return;
    this.isFocused = false;
    this.onTouched();
    this.updateOverlay(this.value);
  }

  private updateOverlay(currentValue: string) {
    const fullFormat = this.getFullFormat();

    const chars: string[] = [];
    const padding: string[] = [];

    for (let i = 0; i < currentValue.length; i++) {
      chars.push(currentValue[i]);
    }

    if (this.isFocused || currentValue.length > 0) {
      for (let i = currentValue.length; i < fullFormat.length; i++) {
        padding.push(fullFormat[i]);
      }
    }

    this.overlayChars = [...chars];
    this.overlayPadding = [...padding];
  }



  private parseValue(value: string): DateTimeWithMilisecondsStruct | null {
    if (!value) return null;

    if (this.onlyYear) {
      const year = Number(value);
      if (isNaN(year) || year < 1000 || year > 9999) return null;
      return { date: { day: 1, month: 1, year } };
    }

    const [datePart, timePart] = value.split(' ');
    const [d, m, y] = datePart.split('.').map(Number);

    const testDate = new Date(y, m - 1, d);
    if (
        testDate.getDate() !== d ||
        testDate.getMonth() !== m - 1 ||
        testDate.getFullYear() !== y
    ) return null;

    const result: DateTimeWithMilisecondsStruct = {
      date: { day: d, month: m, year: y }
    };

    if (this.withTime && timePart) {
      const [hms, msPart] = timePart.split('.');
      const [h, min, s] = hms.split(':').map(Number);

      result.time = {
        hour: h || 0,
        minute: min || 0,
        second: s || 0,
        millisecond: +(msPart ?? 0)
      };
    }

    return result;
  }

  private applyMask(digits: string): string {
    if (this.onlyYear) return digits.substring(0, 4);

    const d = digits.substring(0, 2);
    const m = digits.substring(2, 4);
    const y = digits.substring(4, 8);

    let result = d;
    if (m) result += '.' + m;
    if (y) result += '.' + y;

    if (!this.withTime) return result;

    const h = digits.substring(8, 10);
    const mi = digits.substring(10, 12);
    const s = this.withSeconds ? digits.substring(12, 14) : '';
    const ms = this.withMilliseconds ? digits.substring(14, 17) : '';

    if (h) result += ' ' + h;
    if (mi) result += ':' + mi;
    if (this.withSeconds && s) result += ':' + s;
    if (this.withMilliseconds && ms) result += '.' + ms;

    return result;
  }

  private formatValue(value: DateTimeWithMilisecondsStruct): string {
    if (!value.date) return '';

    if (this.onlyYear) return String(value.date.year);

    let out =
        `${String(value.date.day).padStart(2, '0')}.` +
        `${String(value.date.month).padStart(2, '0')}.` +
        `${String(value.date.year).padStart(4, '0')}`;

    if (this.withTime && value.time) {
      out += ` ${String(value.time.hour).padStart(2, '0')}:` +
          `${String(value.time.minute).padStart(2, '0')}`;

      if (this.withSeconds)
        out += `:${String(value.time.second || 0).padStart(2, '0')}`;

      if (this.withMilliseconds)
        out += `.${String(value.time.millisecond || 0).padStart(3, '0')}`;
    }

    return out;
  }

  private getMaxDigits(): number {
    if (this.onlyYear) return 4;
    let total = 8;
    if (this.withTime) total += 4;
    if (this.withSeconds) total += 2;
    if (this.withMilliseconds) total += 3;
    return total;
  }

  private getFullFormat(): string {
    if (this.onlyYear) return 'YYYY';

    let format = 'DD.MM.YYYY';
    if (this.withTime) format += ' HH:mm';
    if (this.withSeconds) format += ':ss';
    if (this.withMilliseconds) format += '.SSS';
    return format;
  }

  private isInputComplete(value: string): boolean {
    return value.length === this.getFullFormat().length &&
        value.replace(/\D/g, '').length === this.getMaxDigits();
  }

  get control(): FormControl {
    return (
        this.controlContainer?.control?.get(this.formControlName ?? '') as FormControl
    ) ?? new FormControl();
  }

}